import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useWallets } from '../WalletContext';
import './BudgetTracker.css';

function BudgetTracker({ selectedMonth, selectedYear, months, selectedWallets, refreshKey }) {
  const { getSelectedCurrency, getCurrencySymbol } = useWallets();
  const [budgetData, setBudgetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const selectedCurrency = getSelectedCurrency();
  const currencySymbol = getCurrencySymbol(selectedCurrency);

  useEffect(() => {
    if (selectedMonth !== 'all') {
      fetchBudgetData();
    } else {
      setBudgetData(null);
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, selectedWallets, refreshKey]); // Added refreshKey dependency

  const fetchBudgetData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Convert from 0-indexed to 1-indexed (0 = January becomes 1 = January)
      const monthIndex = parseInt(selectedMonth) + 1;
      
      // Prepare wallet IDs parameter - selectedWallets is always an array of numeric IDs
      const walletIds = selectedWallets.length > 0 ? selectedWallets.join(',') : '';
      
      const response = await api.get('/budget/tracking', {
        params: {
          month: monthIndex,
          year: selectedYear,
          wallet_ids: walletIds
        }
      });

      setBudgetData(response.data);
    } catch (error) {
      console.error('Error fetching budget data:', error);
      setError('Failed to load budget tracking data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'good':
        return '#059669';
      case 'warning':
        return '#f59e0b';
      case 'danger':
        return '#dc2626';
      case 'exceeded':
        return '#991b1b';
      default:
        return '#64748b';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'good':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'danger':
        return '🚨';
      case 'exceeded':
        return '❌';
      default:
        return 'ℹ️';
    }
  };

  if (selectedMonth === 'all') {
    return null; // Don't show budget tracker for yearly view
  }

  if (loading) {
    return (
      <div className="budget-tracker">
        <div className="budget-header">
          <h3>📊 Monthly Budget Tracker</h3>
        </div>
        <div className="budget-loading">
          <div className="spinner-small"></div>
          <p>Loading budget data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="budget-tracker">
        <div className="budget-header">
          <h3>📊 Monthly Budget Tracker</h3>
        </div>
        <div className="budget-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!budgetData) {
    return null;
  }

  return (
    <div className="budget-tracker">
      <div className="budget-header">
        <h3>📊 Monthly Budget Tracker - {months.find(m => m.value === selectedMonth)?.label} {selectedYear}</h3>
        <div className="income-display">
          Income: <span className="income-amount">{currencySymbol}{budgetData.total_income.toFixed(2)}</span>
        </div>
      </div>

      {budgetData.alerts.length > 0 && (
        <div className="budget-alerts">
          {budgetData.alerts.map((alert, index) => (
            <div key={index} className="budget-alert">
              {alert}
            </div>
          ))}
        </div>
      )}

      <div className="budget-groups">
        {/* NEEDS */}
        <div className="budget-group needs">
          <div className="group-header">
            <div className="group-title">
              <span className="group-icon">🏠</span>
              <span className="group-name">NEEDS</span>
              <span className="group-percentage">({budgetData.needs_percentage}%)</span>
            </div>
            <div className="group-status">
              {getStatusIcon(budgetData.needs.status)}
            </div>
          </div>

          <div className="group-amounts">
            <div className="amount-item">
              <span className="amount-label">Budget:</span>
              <span className="amount-value">{currencySymbol}{budgetData.needs.budget.toFixed(2)}</span>
            </div>
            <div className="amount-item">
              <span className="amount-label">Spent:</span>
              <span className="amount-value spent">{currencySymbol}{budgetData.needs.spent.toFixed(2)}</span>
            </div>
            <div className="amount-item">
              <span className="amount-label">Remaining:</span>
              <span className={`amount-value ${budgetData.needs.remaining < 0 ? 'negative' : 'positive'}`}>
                {currencySymbol}{budgetData.needs.remaining.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="progress-bar-container">
            <div 
              className="progress-bar"
              style={{
                width: `${Math.min(budgetData.needs.percentage_used, 100)}%`,
                backgroundColor: getStatusColor(budgetData.needs.status)
              }}
            >
              <span className="progress-text">{budgetData.needs.percentage_used.toFixed(1)}%</span>
            </div>
          </div>
        </div>

        {/* WANTS */}
        <div className="budget-group wants">
          <div className="group-header">
            <div className="group-title">
              <span className="group-icon">🎉</span>
              <span className="group-name">WANTS</span>
              <span className="group-percentage">({budgetData.wants_percentage}%)</span>
            </div>
            <div className="group-status">
              {getStatusIcon(budgetData.wants.status)}
            </div>
          </div>

          <div className="group-amounts">
            <div className="amount-item">
              <span className="amount-label">Budget:</span>
              <span className="amount-value">{currencySymbol}{budgetData.wants.budget.toFixed(2)}</span>
            </div>
            <div className="amount-item">
              <span className="amount-label">Spent:</span>
              <span className="amount-value spent">{currencySymbol}{budgetData.wants.spent.toFixed(2)}</span>
            </div>
            <div className="amount-item">
              <span className="amount-label">Remaining:</span>
              <span className={`amount-value ${budgetData.wants.remaining < 0 ? 'negative' : 'positive'}`}>
                {currencySymbol}{budgetData.wants.remaining.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="progress-bar-container">
            <div 
              className="progress-bar"
              style={{
                width: `${Math.min(budgetData.wants.percentage_used, 100)}%`,
                backgroundColor: getStatusColor(budgetData.wants.status)
              }}
            >
              <span className="progress-text">{budgetData.wants.percentage_used.toFixed(1)}%</span>
            </div>
          </div>
        </div>

        {/* SAVINGS */}
        <div className="budget-group savings">
          <div className="group-header">
            <div className="group-title">
              <span className="group-icon">💰</span>
              <span className="group-name">SAVINGS</span>
              <span className="group-percentage">({budgetData.savings_percentage}%)</span>
            </div>
            <div className="group-status">
              {getStatusIcon(budgetData.savings.status)}
            </div>
          </div>

          <div className="group-amounts">
            <div className="amount-item">
              <span className="amount-label">Target:</span>
              <span className="amount-value">{currencySymbol}{budgetData.savings.budget.toFixed(2)}</span>
            </div>
            <div className="amount-item">
              <span className="amount-label">Saved:</span>
              <span className="amount-value spent">{currencySymbol}{budgetData.savings.spent.toFixed(2)}</span>
            </div>
            <div className="amount-item">
              <span className="amount-label">
                {budgetData.savings.remaining >= 0 ? 'To go:' : 'Above:'}
              </span>
              <span className={`amount-value ${budgetData.savings.remaining < 0 ? 'positive' : 'negative'}`}>
                {currencySymbol}{Math.abs(budgetData.savings.remaining).toFixed(2)}
              </span>
            </div>
          </div>

          <div className="progress-bar-container">
            <div 
              className="progress-bar"
              style={{
                width: `${Math.min(budgetData.savings.percentage_used, 100)}%`,
                backgroundColor: getStatusColor(budgetData.savings.status)
              }}
            >
              <span className="progress-text">{budgetData.savings.percentage_used.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>

      <div className={`overall-status ${budgetData.overall_status}`}>
        {budgetData.overall_status === 'good' && '✅ On track for this month'}
        {budgetData.overall_status === 'ok' && 'ℹ️ Monitor your spending'}
        {budgetData.overall_status === 'warning' && '⚠️ Watch your budget carefully'}
      </div>
    </div>
  );
}

export default BudgetTracker;
